import { Dispatch, SetStateAction } from 'react';
import {
  AsinEntry,
  BuyboxAsinField,
  BuyboxAsinMetrics,
  CommerceClient,
  CommercePartnerAccount,
  InventoryEntry,
  InventoryForecastEntry,
  InventoryHistoricalField,
  InventoryToplineMetrics,
  InventoryToplineSellerField,
  InventoryToplineVendorField,
  KeywordAsinField,
  KeywordAsinMetrics,
  KeywordEntry,
  KeywordPerformanceField,
  KeywordPerformanceMetrics,
  KeywordTrendEntry,
  KeywordTrendField,
  KeywordTrendMetrics,
  OverviewAsinField,
  OverviewAsinMetrics,
  OverviewPerformanceDimension,
  OverviewPerformanceMetrics,
  OverviewToplineCoreField,
  OverviewToplineMetrics,
  OverviewToplineSellerField,
  OverviewToplineVendorField,
  PartnerType,
  SellerPerformanceField,
  TimeSeriesEntry,
  VendorPerformanceField
} from '@blisspointmedia/bpm-types/dist/CommerceOps';
import { toPrettyPercent2Decimals } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  FormatterNew,
  MetricFormatter,
  ROAS_FORMATTER,
  SPEND_OR_REVENUE_DIFFERENCE_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyDeltaPercent,
  toPrettyNumber,
  VOLUME_FORMATTER
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import {
  DATE_GROUPING_TYPES,
  DateGroupingType
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { GlossaryItem } from '@Components/GlossaryModal/GlossaryModal';
import { ColumnMetaData } from '@Components/MetricsTable/metrics-table-utils';
import { dateToMddyy } from '@src/utils/date-utils';
import { exportMultiSheetToExcel } from '@src/utils/download-utils';
import { awaitJSON, CommerceOpsLambdaFetch } from '@src/utils/fetch-utils';
import { DateRange } from '@src/Components/DatePicker/date-picker-types';

export const enum PageTab {
  OVERVIEW = 'overview',
  INVENTORY = 'inventory',
  KEYWORDS_AND_TRAFFIC = 'keywords-and-traffic',
  BUYBOX = 'buybox'
}

export const NAVS = [
  { label: 'Overview', key: PageTab.OVERVIEW },
  { label: 'Inventory', key: PageTab.INVENTORY },
  { label: 'Keywords/Traffic', key: PageTab.KEYWORDS_AND_TRAFFIC },
  { label: 'Buybox', key: PageTab.BUYBOX }
];

export interface CommerceOpsTabProps<D> {
  data: D;
  downloadTable: (fileNameSubject: string, dataMap: any) => void;
}

export interface OverviewData {
  primaryTimeSeries: TimeSeriesEntry<OverviewToplineMetrics>[];
  compTimeSeries: TimeSeriesEntry<OverviewToplineMetrics>[];
  performanceData: {
    [OverviewPerformanceDimension.ASIN]: AsinEntry<OverviewPerformanceMetrics>[];
  };
  asinEntries: AsinEntry<OverviewAsinMetrics>[];
}

export interface InventoryData {
  primaryTimeSeries: TimeSeriesEntry<InventoryToplineMetrics>[];
  compTimeSeries: TimeSeriesEntry<InventoryToplineMetrics>[];
  forecast: InventoryForecastEntry<Record<string, number>>[];
  forecastMetricsKeys: string[];
  historical: InventoryEntry<Record<string, number>>[];
}

export interface KeywordData {
  keywordTrends: Record<DateGroupingType, KeywordTrendEntry<KeywordTrendMetrics>[]>;
  keywordPerformance: KeywordEntry<KeywordPerformanceMetrics>[];
  sessionsByAsin: AsinEntry<KeywordAsinMetrics>[];
}

export interface BuyboxData {
  buyboxEntries: AsinEntry<BuyboxAsinMetrics>[];
}

export interface OverviewTabProps extends CommerceOpsTabProps<OverviewData> {
  config: {
    sparkChartOptions: {
      value:
        | OverviewToplineCoreField
        | OverviewToplineVendorField
        | OverviewToplineSellerField
        | OverviewAsinField;
      label: string;
      disabled: boolean;
    }[];
    performanceConfigMap: Record<string, DimensionConfig<string>>;
  };
}

export interface InventoryTabProps extends CommerceOpsTabProps<InventoryData> {
  primaryDateRange: DateRange;
  config: {
    sparkChartOptions: {
      value: InventoryToplineVendorField | InventoryToplineSellerField;
      label: string;
      disabled: boolean;
    }[];
    sparkChartDefaults: InventoryToplineVendorField[] | InventoryToplineSellerField[];
  };
}

export type KeywordTabProps = CommerceOpsTabProps<KeywordData>;

export type BuyboxTabProps = CommerceOpsTabProps<BuyboxData>;

export const GLOSSARY: GlossaryItem[] = [
  {
    term: 'ASIN',
    definition:
      'The Amazon Standard Identification Number; a unique 10-character identifier for a specific product.'
  },
  {
    term: 'Product Name',
    definition: 'The public-facing title of the listing as it appears on the detail page.'
  },
  {
    term: 'Session',
    definition: 'A unique visit to a product page by a user within a 24-hour window.'
  },
  {
    term: 'Total Sales',
    definition: 'The gross value of units ordered (Ordered Product Sales) before returns.'
  },
  {
    term: 'Total Orders',
    definition: 'The total count of unique orders placed by customers.'
  },
  {
    term: 'Organic Sales',
    definition: 'Sales generated through standard search results (Total Sales minus Ad Sales).'
  },
  {
    term: 'Paid/Ad Sales',
    definition: 'Sales directly attributed to clicks on Amazon Advertising campaigns.'
  },
  {
    term: 'Ad Spend',
    definition: 'The total cost incurred for advertising spend.'
  },
  {
    term: 'ROAS',
    definition:
      'Return on Ad Spend. The amount of revenue from paid media campaigns divided by the ad spend of those campaigns.'
  },
  {
    term: 'TROAS',
    definition: 'Total Return on Ad Spend. Total Sales / Ad Spend.'
  },
  {
    term: 'TACOS',
    definition: 'Total Advertising Cost of Sales; calculated as (Ad Spend / Total Sales).'
  },
  {
    term: 'Net PPM',
    definition: 'Net Pure Product Margin.'
  },
  {
    term: 'Current Inventory (Forecast)',
    definition:
      'The total number of units currently "on hand" and available for sale at a specific moment.'
  },
  {
    term: 'Current Inventory (Historical)',
    definition: 'A record of past stock levels used to analyze trends and stockouts.'
  },
  {
    term: 'Weeks of Cover',
    definition:
      'The estimated number of weeks current stock will last based on recent sales velocity.'
  },
  {
    term: 'Total Units Sold (Forecast)',
    definition: 'The projected units sold based on the average of the last 4 weeks.'
  },
  {
    term: 'Total Units Sold (Historical)',
    definition: 'The amount of units sold for each ASIN during the given week.'
  },
  {
    term: 'Buy Box %',
    definition: 'The percentage of time your offer was the "Featured Offer" on the detail page.'
  },
  {
    term: 'Lost Buybox',
    definition: 'The instances where the Buy Box was lost to a competitor or hidden due to pricing.'
  }
];

export const OVERVIEW_METRIC_TO_PRETTY_NAME: Record<string, string> = {
  [OverviewToplineCoreField.TOTAL_UNITS_SOLD]: 'Total Units Sold',
  [OverviewToplineCoreField.TOTAL_SALES]: 'Total Sales',
  [OverviewToplineCoreField.PAID_SALES]: 'Paid Sales',
  [OverviewToplineCoreField.ORGANIC_SALES]: 'Organic Sales',
  [OverviewToplineCoreField.TACOS]: 'TACoS',
  [OverviewToplineCoreField.TROAS]: 'TROAS',
  [OverviewToplineCoreField.AD_SPEND]: 'Ad Spend',
  [OverviewToplineVendorField.GLANCE_VIEWS]: 'Glance Views',
  [OverviewToplineSellerField.SESSIONS]: 'Sessions',
  [OverviewToplineSellerField.BUYBOX_WINS]: 'Buybox Wins',
  [OverviewToplineSellerField.BUYBOX_PERCENTAGE]: 'Buybox Win %',
  [OverviewAsinField.DIFFERENCE]: 'Difference (in $)',
  [OverviewAsinField.PERCENT_CHANGE]: '% Change'
} as const;

export const INVENTORY_METRIC_TO_PRETTY_NAME: Record<string, string> = {
  [InventoryToplineVendorField.GLANCE_VIEWS]: 'Glance Views',
  [InventoryToplineSellerField.SESSIONS]: 'Sessions',
  [InventoryToplineSellerField.PAGE_VIEWS]: 'Page Views',
  [InventoryToplineSellerField.TOTAL_UNITS_SOLD]: 'Total Units Sold',
  [InventoryToplineSellerField.TOTAL_SALES]: 'Total Sales',
  [InventoryToplineSellerField.BUYBOX_WINS]: 'Buybox Wins',
  [InventoryToplineSellerField.BUYBOX_PERCENTAGE]: 'Buybox Win %',
  [InventoryHistoricalField.ON_HAND_INVENTORY]: 'Current Inventory',
  [InventoryHistoricalField.PAID_SALES]: 'Paid Sales',
  [InventoryHistoricalField.AD_SPEND]: 'Ad Spend',
  [InventoryHistoricalField.ROAS]: 'ROAS'
} as const;

export const KEYWORD_METRIC_TO_PRETTY_NAME: Record<string, string> = {
  [KeywordPerformanceField.CLICKS]: 'Clicks',
  [KeywordPerformanceField.IMPRESSIONS]: 'Impressions',
  [KeywordPerformanceField.AD_SPEND]: 'Ad Spend',
  [KeywordPerformanceField.PAID_SALES]: 'Paid Sales',
  [KeywordPerformanceField.ROAS]: 'ROAS',
  [KeywordAsinField.SESSIONS]: 'Sessions',
  [KeywordAsinField.PERCENT_CHANGE]: '% Change'
} as const;

export const BUYBOX_METRIC_TO_PRETTY_NAME: Record<string, string> = {
  [BuyboxAsinField.WIN_PERCENTAGE]: 'Current Buy Box Win %',
  [BuyboxAsinField.DIFFERENCE]: 'Buy Box Difference (PoP)'
} as const;

const buildSparkChartOptions = <TEnabled extends string, TDisabled extends string = never>(
  metricToPrettyName: Record<string, string>,
  enabled: TEnabled[],
  disabled: TDisabled[] = []
): { value: TEnabled | TDisabled; label: string; disabled: boolean }[] => [
  ...enabled.map(value => ({
    value,
    label: metricToPrettyName[value],
    disabled: false
  })),
  ...disabled.map(value => ({
    value,
    label: metricToPrettyName[value],
    disabled: true
  }))
];

export const OVERVIEW_TOPLINE_METADATA_MAP: Record<string, ColumnMetaData> = {
  [OverviewToplineCoreField.TACOS]: {
    prettyName: OVERVIEW_METRIC_TO_PRETTY_NAME[OverviewToplineCoreField.TACOS],
    aggregator: totals =>
      totals[OverviewToplineCoreField.AD_SPEND] && totals[OverviewToplineCoreField.TOTAL_SALES]
        ? (totals[OverviewToplineCoreField.AD_SPEND] as number) /
          (totals[OverviewToplineCoreField.TOTAL_SALES] as number)
        : null
  },
  [OverviewToplineCoreField.TROAS]: {
    prettyName: OVERVIEW_METRIC_TO_PRETTY_NAME[OverviewToplineCoreField.TROAS],
    aggregator: totals =>
      totals[OverviewToplineCoreField.AD_SPEND] && totals[OverviewToplineCoreField.TOTAL_SALES]
        ? (totals[OverviewToplineCoreField.TOTAL_SALES] as number) /
          (totals[OverviewToplineCoreField.AD_SPEND] as number)
        : null
  },
  [OverviewToplineSellerField.BUYBOX_PERCENTAGE]: {
    prettyName: OVERVIEW_METRIC_TO_PRETTY_NAME[OverviewToplineSellerField.BUYBOX_PERCENTAGE],
    aggregator: totals =>
      totals[OverviewToplineSellerField.BUYBOX_WINS] && totals[OverviewToplineSellerField.SESSIONS]
        ? (totals[OverviewToplineSellerField.BUYBOX_WINS] as number) /
          (totals[OverviewToplineSellerField.SESSIONS] as number)
        : null
  }
} as const;

export const INVENTORY_TOPLINE_METADATA_MAP: Record<string, ColumnMetaData> = {
  [InventoryToplineSellerField.BUYBOX_PERCENTAGE]: {
    prettyName: INVENTORY_METRIC_TO_PRETTY_NAME[InventoryToplineSellerField.BUYBOX_PERCENTAGE],
    aggregator: totals =>
      totals[InventoryToplineSellerField.BUYBOX_WINS] &&
      totals[InventoryToplineSellerField.SESSIONS]
        ? (totals[InventoryToplineSellerField.BUYBOX_WINS] as number) /
          (totals[InventoryToplineSellerField.SESSIONS] as number)
        : null
  }
} as const;

export const constructMetricFormatter =
  (
    metricToFormatter: Record<string, FormatterNew>,
    defaultFormatter: FormatterNew
  ): MetricFormatter =>
  (metric: string) => {
    if (metricToFormatter[metric]) {
      return metricToFormatter[metric];
    }
    return defaultFormatter;
  };

export const OVERVIEW_METRIC_TO_FORMATTER: Record<string, FormatterNew> = {
  [OverviewToplineCoreField.TOTAL_UNITS_SOLD]: VOLUME_FORMATTER,
  [OverviewToplineCoreField.TOTAL_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [OverviewToplineCoreField.AD_SPEND]: SPEND_OR_REVENUE_FORMATTER,
  [OverviewToplineCoreField.PAID_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [OverviewToplineCoreField.ORGANIC_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [OverviewToplineCoreField.TACOS]: ROAS_FORMATTER,
  [OverviewToplineCoreField.TROAS]: ROAS_FORMATTER,
  [OverviewToplineVendorField.GLANCE_VIEWS]: VOLUME_FORMATTER,
  [OverviewToplineSellerField.SESSIONS]: VOLUME_FORMATTER,
  [OverviewToplineSellerField.BUYBOX_WINS]: VOLUME_FORMATTER,
  [OverviewToplineSellerField.BUYBOX_PERCENTAGE]: toPrettyPercent2Decimals,
  [OverviewAsinField.DIFFERENCE]: SPEND_OR_REVENUE_DIFFERENCE_FORMATTER,
  [OverviewAsinField.PERCENT_CHANGE]: toPrettyDeltaPercent
};

export const INVENTORY_METRIC_TO_FORMATTER: Record<string, FormatterNew> = {
  [InventoryToplineSellerField.SESSIONS]: VOLUME_FORMATTER,
  [InventoryToplineSellerField.PAGE_VIEWS]: VOLUME_FORMATTER,
  [InventoryToplineSellerField.TOTAL_UNITS_SOLD]: VOLUME_FORMATTER,
  [InventoryToplineSellerField.TOTAL_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [InventoryToplineSellerField.BUYBOX_WINS]: VOLUME_FORMATTER,
  [InventoryToplineSellerField.BUYBOX_PERCENTAGE]: toPrettyPercent2Decimals,
  [InventoryHistoricalField.ON_HAND_INVENTORY]: (value, options) =>
    VOLUME_FORMATTER(value, { ...options, zeroLabel: '0' }),
  [InventoryHistoricalField.PAID_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [InventoryHistoricalField.AD_SPEND]: SPEND_OR_REVENUE_FORMATTER,
  [InventoryHistoricalField.ROAS]: ROAS_FORMATTER
};

export const KEYWORD_METRIC_TO_FORMATTER: Record<string, FormatterNew> = {
  [KeywordPerformanceField.CLICKS]: VOLUME_FORMATTER,
  [KeywordPerformanceField.IMPRESSIONS]: VOLUME_FORMATTER,
  [KeywordPerformanceField.AD_SPEND]: SPEND_OR_REVENUE_FORMATTER,
  [KeywordPerformanceField.PAID_SALES]: SPEND_OR_REVENUE_FORMATTER,
  [KeywordPerformanceField.ROAS]: ROAS_FORMATTER,
  [KeywordAsinField.SESSIONS]: VOLUME_FORMATTER,
  [KeywordAsinField.PERCENT_CHANGE]: toPrettyPercent2Decimals
};

export const BUYBOX_METRIC_TO_FORMATTER: Record<string, FormatterNew> = {
  [BuyboxAsinField.WIN_PERCENTAGE]: toPrettyPercent2Decimals,
  [BuyboxAsinField.DIFFERENCE]: toPrettyDeltaPercent
};

// dimension refers to potential commerce ops table dropdown options

export enum AsinDimension {
  ASIN = 'asin'
}

// descriptive field refers to the non-metric strings associated with table entries
// this delineation b/w dimension and descriptive field
// was necessary to support non-dimension strings as table data (see asin)

export enum AsinDescriptiveField {
  ASIN = 'asin',
  PRODUCT_NAME = 'productName'
}

export const ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME = {
  [AsinDescriptiveField.ASIN]: 'ASIN',
  [AsinDescriptiveField.PRODUCT_NAME]: 'Product Name'
};

export const OVERVIEW_TOP_ASINS_DIMENSION_CONFIG_MAP: {
  [AsinDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [AsinDimension.ASIN]: {
    dimensionPrettyName: 'Top ASINs',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: OverviewAsinField,
    metricToPrettyName: OVERVIEW_METRIC_TO_PRETTY_NAME,
    metricToFormatter: OVERVIEW_METRIC_TO_FORMATTER,
    orderBy: { [OverviewAsinField.TOTAL_SALES]: { ascending: false, sortPriority: 0 } }
  }
};

export const OVERVIEW_BIGGEST_MOVERS_DIMENSION_CONFIG_MAP: {
  [AsinDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [AsinDimension.ASIN]: {
    dimensionPrettyName: 'Biggest Movers',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: OverviewAsinField,
    metricToPrettyName: OVERVIEW_METRIC_TO_PRETTY_NAME,
    metricToFormatter: OVERVIEW_METRIC_TO_FORMATTER,
    orderBy: { [OverviewAsinField.DIFFERENCE]: { ascending: false, sortPriority: 0 } }
  }
};

export interface DimensionConfig<T extends string> {
  dimensionPrettyName: string;
  dimensionSuffix?: string;
  disabled?: boolean;
  descriptiveFields: T[];
  descriptiveFieldToPrettyName: Record<T, string>;
  descriptiveFieldToFormatter?: Record<T, (val: any) => string>;
  descriptiveFieldWidth?: Partial<Record<T, number>>;
  metricsEnum: Record<string, string>;
  metricToPrettyName: Record<string, string>;
  metricToFormatter?: Record<string, FormatterNew>;
  rowGroupingField?: T;
  rowFormatter?: (row: any, field: string) => string;
  hiddenGlobalKpis?: Set<string>;
  toggles?: {
    kpis: { field: string; label: string }[];
    onKpiToggle: (
      kpi: { field: string; label: string },
      hiddenKpis: Set<string>,
      setHiddenKpis: Dispatch<SetStateAction<Set<string>>>
    ) => void;
  };
  orderBy?: Record<string, { ascending: boolean; sortPriority: number }>;
}

export const VENDOR_PERFORMANCE_DIMENSION_CONFIG_MAP: {
  [OverviewPerformanceDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [OverviewPerformanceDimension.ASIN]: {
    dimensionPrettyName: 'Asin Performance',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: VendorPerformanceField,
    metricToPrettyName: OVERVIEW_METRIC_TO_PRETTY_NAME,
    metricToFormatter: OVERVIEW_METRIC_TO_FORMATTER,
    orderBy: { [VendorPerformanceField.TOTAL_SALES]: { ascending: false, sortPriority: 0 } }
  }
};

export const SELLER_PERFORMANCE_DIMENSION_CONFIG_MAP: {
  [OverviewPerformanceDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [OverviewPerformanceDimension.ASIN]: {
    dimensionPrettyName: 'Asin Performance',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: SellerPerformanceField,
    metricToPrettyName: OVERVIEW_METRIC_TO_PRETTY_NAME,
    metricToFormatter: OVERVIEW_METRIC_TO_FORMATTER,
    orderBy: { [SellerPerformanceField.TOTAL_SALES]: { ascending: false, sortPriority: 0 } }
  }
};

export const KEYWORD_SESSIONS_BY_ASIN_DIMENSION_CONFIG_MAP: {
  [AsinDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [AsinDimension.ASIN]: {
    dimensionPrettyName: 'Sessions by ASIN',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: KeywordAsinField,
    metricToPrettyName: KEYWORD_METRIC_TO_PRETTY_NAME,
    metricToFormatter: KEYWORD_METRIC_TO_FORMATTER,
    orderBy: {
      [KeywordAsinField.SESSIONS]: { ascending: false, sortPriority: 0 }
    }
  }
};

export const BUYBOX_WIN_PERCENTAGE_DIMENSION_CONFIG_MAP: {
  [AsinDimension.ASIN]: DimensionConfig<AsinDescriptiveField>;
} = {
  [AsinDimension.ASIN]: {
    dimensionPrettyName: 'Buybox Win Percentage by Product',
    descriptiveFields: [AsinDescriptiveField.ASIN, AsinDescriptiveField.PRODUCT_NAME],
    descriptiveFieldToPrettyName: ASIN_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: BuyboxAsinField,
    metricToPrettyName: BUYBOX_METRIC_TO_PRETTY_NAME,
    metricToFormatter: BUYBOX_METRIC_TO_FORMATTER
  }
};

export enum InventoryDimension {
  FORECAST = 'forecast',
  HISTORICAL = 'historical'
}

export enum InventoryForecastDescriptiveField {
  ASIN = 'asin',
  PRODUCT_NAME = 'productName',
  ON_HAND_WEEKS_OF_COVER = 'onHandWeeksOfCover',
  ON_HAND_STATUS = 'onHandStatus',
  KPI = 'kpi'
}

export const INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_PRETTY_NAME = {
  [InventoryForecastDescriptiveField.ASIN]: 'ASIN',
  [InventoryForecastDescriptiveField.PRODUCT_NAME]: 'Product Name',
  [InventoryForecastDescriptiveField.ON_HAND_WEEKS_OF_COVER]: 'Current Weeks of Cover',
  [InventoryForecastDescriptiveField.ON_HAND_STATUS]: 'Current Status',
  [InventoryForecastDescriptiveField.KPI]: 'Metric'
};

export const INVENTORY_FORECAST_DESCRIPTIVE_FIELD_TO_FORMATTER = {
  [InventoryForecastDescriptiveField.ASIN]: (val: string): string => val,
  [InventoryForecastDescriptiveField.PRODUCT_NAME]: (val: string): string => val,
  [InventoryForecastDescriptiveField.ON_HAND_WEEKS_OF_COVER]: (val: number): string =>
    toPrettyNumber(val, { decimals: 1 }),
  [InventoryForecastDescriptiveField.ON_HAND_STATUS]: (val: string): string => val,
  [InventoryForecastDescriptiveField.KPI]: (val: string): string =>
    INVENTORY_METRIC_TO_PRETTY_NAME[val]
};

export enum InventoryHistoricalDescriptiveField {
  ASIN = 'asin',
  PRODUCT_NAME = 'productName',
  KPI = 'kpi'
}

export const INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_PRETTY_NAME = {
  [InventoryHistoricalDescriptiveField.ASIN]: 'ASIN',
  [InventoryHistoricalDescriptiveField.PRODUCT_NAME]: 'Product Name',
  [InventoryHistoricalDescriptiveField.KPI]: 'Metric'
};

export const INVENTORY_HISTORICAL_DESCRIPTIVE_FIELD_TO_FORMATTER = {
  [InventoryHistoricalDescriptiveField.ASIN]: (val: string): string => val,
  [InventoryHistoricalDescriptiveField.PRODUCT_NAME]: (val: string): string => val,
  [InventoryHistoricalDescriptiveField.KPI]: (val: string): string =>
    INVENTORY_METRIC_TO_PRETTY_NAME[val]
};

export enum KeywordTrendDimension {
  KEYWORD = 'keyword'
}

export enum KeywordTrendDescriptiveField {
  KEYWORD = 'keyword',
  DATE = 'date'
}

export const KEYWORD_TREND_DESCRIPTIVE_FIELD_TO_PRETTY_NAME = {
  [KeywordTrendDescriptiveField.KEYWORD]: 'Keyword',
  [KeywordTrendDescriptiveField.DATE]: 'Date'
};

export const KEYWORD_TREND_DESCRIPTIVE_FIELD_TO_FORMATTER = {
  [KeywordTrendDescriptiveField.KEYWORD]: (val: string): string => val,
  [KeywordTrendDescriptiveField.DATE]: dateToMddyy
};

export const KEYWORD_TREND_DIMENSION_CONFIG_MAP: {
  [KeywordTrendDimension.KEYWORD]: DimensionConfig<KeywordTrendDescriptiveField>;
} = {
  [KeywordTrendDimension.KEYWORD]: {
    dimensionPrettyName: 'Search Trend of Top Keywords',
    descriptiveFields: [KeywordTrendDescriptiveField.KEYWORD, KeywordTrendDescriptiveField.DATE],
    descriptiveFieldToPrettyName: KEYWORD_TREND_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    descriptiveFieldToFormatter: KEYWORD_TREND_DESCRIPTIVE_FIELD_TO_FORMATTER,
    metricsEnum: KeywordTrendField,
    metricToPrettyName: KEYWORD_METRIC_TO_PRETTY_NAME,
    metricToFormatter: KEYWORD_METRIC_TO_FORMATTER,
    orderBy: {
      [KeywordTrendDescriptiveField.KEYWORD]: { ascending: true, sortPriority: 0 },
      [KeywordTrendDescriptiveField.DATE]: { ascending: true, sortPriority: 1 }
    }
  }
};

export enum KeywordPerformanceDimension {
  KEYWORD = 'keyword'
}

export enum KeywordPerformanceDescriptiveField {
  KEYWORD = 'keyword'
}

export const KEYWORD_PERFORMANCE_DESCRIPTIVE_FIELD_TO_PRETTY_NAME = {
  [KeywordPerformanceDescriptiveField.KEYWORD]: 'Keyword'
};

export const KEYWORD_PERFORMANCE_DIMENSION_CONFIG_MAP: {
  [KeywordPerformanceDimension.KEYWORD]: DimensionConfig<KeywordPerformanceDescriptiveField>;
} = {
  [KeywordPerformanceDimension.KEYWORD]: {
    dimensionPrettyName: 'Top Keywords Performance',
    descriptiveFields: [KeywordPerformanceDescriptiveField.KEYWORD],
    descriptiveFieldToPrettyName: KEYWORD_PERFORMANCE_DESCRIPTIVE_FIELD_TO_PRETTY_NAME,
    metricsEnum: KeywordPerformanceField,
    metricToPrettyName: KEYWORD_METRIC_TO_PRETTY_NAME,
    metricToFormatter: KEYWORD_METRIC_TO_FORMATTER,
    orderBy: {
      [KeywordPerformanceField.IMPRESSIONS]: { ascending: false, sortPriority: 0 }
    }
  }
};

export const DATE_GROUPING_TYPES_TO_PRETTY_NAME: Record<DateGroupingType, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly'
};

export const DATE_GROUPING_OPTIONS = DATE_GROUPING_TYPES.map(type => ({
  value: type,
  label: DATE_GROUPING_TYPES_TO_PRETTY_NAME[type]
}));

export interface PartnerTypeUIConfig {
  tabs: {
    [PageTab.OVERVIEW]: { disabled: boolean };
    [PageTab.INVENTORY]: { disabled: boolean };
    [PageTab.KEYWORDS_AND_TRAFFIC]: { disabled: boolean };
    [PageTab.BUYBOX]: { disabled: boolean };
  };
  overview: {
    sparkChartOptions: {
      value:
        | OverviewToplineCoreField
        | OverviewToplineVendorField
        | OverviewToplineSellerField
        | OverviewAsinField;
      label: string;
      disabled: boolean;
    }[];
    performanceConfigMap: Record<string, DimensionConfig<string>>;
  };
  inventory: {
    sparkChartOptions: {
      value: InventoryToplineVendorField | InventoryToplineSellerField;
      label: string;
      disabled: boolean;
    }[];
    sparkChartDefaults: InventoryToplineVendorField[] | InventoryToplineSellerField[];
  };
}

export const PARTNER_TYPE_UI_CONFIG_MAP: Record<PartnerType, PartnerTypeUIConfig> = {
  seller: {
    tabs: {
      [PageTab.OVERVIEW]: { disabled: false },
      [PageTab.INVENTORY]: { disabled: false },
      [PageTab.KEYWORDS_AND_TRAFFIC]: { disabled: false },
      [PageTab.BUYBOX]: { disabled: false }
    },
    [PageTab.OVERVIEW]: {
      sparkChartOptions: buildSparkChartOptions<
        OverviewToplineCoreField | OverviewToplineSellerField,
        OverviewToplineVendorField
      >(
        OVERVIEW_METRIC_TO_PRETTY_NAME,
        [
          OverviewToplineCoreField.TOTAL_UNITS_SOLD,
          OverviewToplineCoreField.TOTAL_SALES,
          OverviewToplineCoreField.AD_SPEND,
          OverviewToplineCoreField.PAID_SALES,
          OverviewToplineCoreField.ORGANIC_SALES,
          OverviewToplineCoreField.TACOS,
          OverviewToplineCoreField.TROAS,
          OverviewToplineSellerField.SESSIONS,
          OverviewToplineSellerField.BUYBOX_WINS,
          OverviewToplineSellerField.BUYBOX_PERCENTAGE
        ],
        [OverviewToplineVendorField.GLANCE_VIEWS]
      ),
      performanceConfigMap: {
        ...SELLER_PERFORMANCE_DIMENSION_CONFIG_MAP
      }
    },
    [PageTab.INVENTORY]: {
      sparkChartOptions: buildSparkChartOptions<
        InventoryToplineSellerField,
        InventoryToplineVendorField
      >(
        INVENTORY_METRIC_TO_PRETTY_NAME,
        [
          InventoryToplineSellerField.SESSIONS,
          InventoryToplineSellerField.PAGE_VIEWS,
          InventoryToplineSellerField.TOTAL_UNITS_SOLD,
          InventoryToplineSellerField.TOTAL_SALES,
          InventoryToplineSellerField.BUYBOX_WINS,
          InventoryToplineSellerField.BUYBOX_PERCENTAGE
        ],
        [InventoryToplineVendorField.GLANCE_VIEWS]
      ),
      sparkChartDefaults: [
        InventoryToplineSellerField.SESSIONS,
        InventoryToplineSellerField.TOTAL_UNITS_SOLD,
        InventoryToplineSellerField.TOTAL_SALES,
        InventoryToplineSellerField.BUYBOX_PERCENTAGE
      ]
    }
  },
  vendor: {
    tabs: {
      [PageTab.OVERVIEW]: { disabled: false },
      [PageTab.INVENTORY]: { disabled: false },
      [PageTab.KEYWORDS_AND_TRAFFIC]: { disabled: false },
      [PageTab.BUYBOX]: { disabled: true }
    },
    [PageTab.OVERVIEW]: {
      sparkChartOptions: buildSparkChartOptions<
        OverviewToplineCoreField | OverviewToplineVendorField,
        OverviewToplineSellerField
      >(
        OVERVIEW_METRIC_TO_PRETTY_NAME,
        [
          OverviewToplineCoreField.TOTAL_UNITS_SOLD,
          OverviewToplineCoreField.TOTAL_SALES,
          OverviewToplineCoreField.AD_SPEND,
          OverviewToplineCoreField.PAID_SALES,
          OverviewToplineCoreField.ORGANIC_SALES,
          OverviewToplineCoreField.TACOS,
          OverviewToplineCoreField.TROAS,
          OverviewToplineVendorField.GLANCE_VIEWS
        ],
        [
          OverviewToplineSellerField.SESSIONS,
          OverviewToplineSellerField.BUYBOX_WINS,
          OverviewToplineSellerField.BUYBOX_PERCENTAGE
        ]
      ),
      performanceConfigMap: VENDOR_PERFORMANCE_DIMENSION_CONFIG_MAP
    },
    [PageTab.INVENTORY]: {
      sparkChartOptions: buildSparkChartOptions<
        InventoryToplineVendorField,
        InventoryToplineSellerField
      >(
        INVENTORY_METRIC_TO_PRETTY_NAME,
        [InventoryToplineVendorField.GLANCE_VIEWS],
        [
          InventoryToplineSellerField.SESSIONS,
          InventoryToplineSellerField.PAGE_VIEWS,
          InventoryToplineSellerField.TOTAL_UNITS_SOLD,
          InventoryToplineSellerField.TOTAL_SALES,
          InventoryToplineSellerField.BUYBOX_WINS,
          InventoryToplineSellerField.BUYBOX_PERCENTAGE
        ]
      ),
      sparkChartDefaults: [InventoryToplineVendorField.GLANCE_VIEWS]
    }
  }
};

export const onKpiToggle = (params: {
  kpi: { field: string; label: string };
  tableLabel: string;
  hiddenKpis: Set<string>;
  setHiddenKpis: Dispatch<SetStateAction<Set<string>>>;
  setShowToast: Dispatch<SetStateAction<boolean>>;
  setToastLabel: Dispatch<SetStateAction<string>>;
}): void => {
  const { kpi, tableLabel, hiddenKpis, setHiddenKpis, setShowToast, setToastLabel } = params;
  const next = new Set(hiddenKpis);
  if (next.has(kpi.field)) {
    next.delete(kpi.field);
    setToastLabel(`${kpi.label} row added to ${tableLabel}`);
    setShowToast(true);
  } else {
    setToastLabel(`${kpi.label} row removed from ${tableLabel}`);
    setShowToast(true);
    next.add(kpi.field);
  }
  setHiddenKpis(next);
};

export const commerceOpsTableDownload = <
  K extends string,
  D extends { metrics: Record<string, number>; [key: string]: any }[]
>(
  fileNameSubject: string,
  dateRange: DateRange,
  dataMap: Record<K, D>
): void => {
  const exportData: {
    name: string;
    data: Record<string, string | number>[];
  }[] = [];
  const fileName = `${fileNameSubject}_${dateRange.start}_${dateRange.end}`;
  (Object.entries(dataMap) as [K, D][]).forEach(([key, nestedData]) => {
    const sheetName = key;
    const flatData = nestedData.map(({ metrics, ...descriptiveFields }) => ({
      ...descriptiveFields,
      ...metrics
    }));
    exportData.push({ name: sheetName, data: flatData });
  });

  exportMultiSheetToExcel(exportData, fileName);
};

export const fetchCommerceClients = async (cid: string): Promise<CommerceClient[]> => {
  const res = await CommerceOpsLambdaFetch('/fetchCommerceAccounts', {
    method: 'POST',
    body: {
      cid
    }
  });
  return awaitJSON<CommerceClient[]>(res);
};

export const fetchOverviewData = async (
  cid: string,
  clientName: string,
  partnerAccount: CommercePartnerAccount,
  primaryDateRange: DateRange,
  comparisonDateRange: DateRange
): Promise<OverviewData> => {
  const res = await CommerceOpsLambdaFetch('/fetchOverviewData', {
    method: 'POST',
    body: {
      cid,
      clientName,
      partnerAccount,
      primaryDateRange,
      comparisonDateRange
    }
  });
  return awaitJSON<OverviewData>(res);
};

export const fetchInventoryData = async (
  cid: string,
  clientName: string,
  partnerAccount: CommercePartnerAccount,
  primaryDateRange: DateRange,
  comparisonDateRange: DateRange
): Promise<InventoryData> => {
  const res = await CommerceOpsLambdaFetch('/fetchInventoryData', {
    method: 'POST',
    body: {
      cid,
      clientName,
      partnerAccount,
      primaryDateRange,
      comparisonDateRange
    }
  });
  return awaitJSON<InventoryData>(res);
};

export const fetchKeywordData = async (
  cid: string,
  clientName: string,
  partnerAccount: CommercePartnerAccount,
  primaryDateRange: DateRange,
  comparisonDateRange: DateRange
): Promise<KeywordData> => {
  const res = await CommerceOpsLambdaFetch('/fetchKeywordData', {
    method: 'POST',
    body: {
      cid,
      clientName,
      partnerAccount,
      primaryDateRange,
      comparisonDateRange
    }
  });
  return awaitJSON<KeywordData>(res);
};

export const fetchBuyboxData = async (
  cid: string,
  clientName: string,
  partnerAccount: CommercePartnerAccount,
  primaryDateRange: DateRange,
  comparisonDateRange: DateRange
): Promise<BuyboxData> => {
  const res = await CommerceOpsLambdaFetch('/fetchBuyboxData', {
    method: 'POST',
    body: {
      cid,
      clientName,
      partnerAccount,
      primaryDateRange,
      comparisonDateRange
    }
  });
  return awaitJSON<BuyboxData>(res);
};
